/**
 * Data layer for the COMPANY panel's Challenges insights section, backed by
 * the company/v1 challenges-hub endpoints (token-scoped to the company; the
 * B2B/Members toggle is appended by api/companyApi.js on every call).
 *
 * Read-only dashboards resolve `null` on failure so pages render placeholders;
 * list fetchers resolve an empty page so tables render their empty state.
 *
 * VOLUME, not STEPS: every tracked figure is called `volume` and carries its
 * own `primaryDataTypeLabel` / `unit`, because the tracker a challenge reads
 * is a property of the challenge. Steps is what that resolves to today.
 */

import {
  getCompanyChallengesDashboard,
  getCompanyChallengesList,
  getCompanyChallengesUsers,
  getCompanyChallengesUserDetail
} from '../../../../api/companyApi';
import { unwrap } from '../../../SleepHub/sleepHubApi';

export { formatBigNumber, lastNDaysLabels } from '../../../SleepHub/sleepHubApi';

/**
 * Exported so the super-admin Challenges hub shapes the same payload rather
 * than restating the field mapping.
 */
export const adaptDashboard = (data) => ({
  hasChallenges: !!data.hasChallenges,
  primaryDataType: data.primaryDataType || 'steps',
  primaryDataTypeLabel: data.primaryDataTypeLabel || 'Steps',
  unit: data.unit || null,

  // The programme
  totalChallenges: data.totalChallenges ?? 0,
  runningChallenges: data.runningChallenges ?? 0,
  upcomingChallenges: data.upcomingChallenges ?? 0,
  endedChallenges: data.endedChallenges ?? 0,
  draftChallenges: data.draftChallenges ?? 0,
  totalTasks: data.totalTasks ?? 0,
  trackedTasks: data.trackedTasks ?? 0,

  // The people
  totalUsers: data.totalUsers ?? 0,
  participants: data.participants ?? 0,
  reachPercentage: data.reachPercentage ?? 0,
  totalJoins: data.totalJoins ?? 0,
  completedJoins: data.completedJoins ?? 0,
  completionRate: data.completionRate ?? 0,
  avgChallengesPerParticipant: data.avgChallengesPerParticipant ?? 0,
  wearableConnected: data.wearableConnected ?? 0,
  wearableConnectedPercentage: data.wearableConnectedPercentage ?? 0,

  // The tracked activity
  totalVolume: data.totalVolume ?? 0,
  trackedRecords: data.trackedRecords ?? 0,
  activeDays: data.activeDays ?? 0,
  avgVolumePerParticipant: data.avgVolumePerParticipant ?? 0,
  avgDailyVolume: data.avgDailyVolume ?? 0,
  bestDayVolume: data.bestDayVolume ?? 0,
  bestDayDate: data.bestDayDate || null,
  targetsHit: data.targetsHit ?? 0,
  targetHitRate: data.targetHitRate ?? 0,

  // What is on offer
  rewardsOffered: data.rewardsOffered ?? 0,
  wellPointsOnOffer: data.wellPointsOnOffer ?? 0,
  winnerSlots: data.winnerSlots ?? 0,
  winnersAnnounced: data.winnersAnnounced ?? 0,

  mostActiveWeekday: data.mostActiveWeekday || null,
  commonDailyVolume: data.commonDailyVolume || null,

  // Backend zero-fills the last 30 days; charts consume plain series.
  joinsPerDay: (data.joinsPerDay || []).map((day) => day.joins ?? 0),
  completionsPerDay: (data.completionsPerDay || []).map((day) => day.completions ?? 0),
  volumePerDay: (data.volumePerDay || []).map((day) => day.volume ?? 0),
  participantsPerDay: (data.volumePerDay || []).map((day) => day.participants ?? 0),

  phaseDistribution: data.phaseDistribution || [],
  statusDistribution: data.statusDistribution || [],
  featureSplit: data.featureSplit || [],
  trackerSplit: data.trackerSplit || [],
  volumeDistribution: data.volumeDistribution || [],
  dailyVolumeDistribution: data.dailyVolumeDistribution || [],
  achievementDistribution: data.achievementDistribution || [],
  activeDaysDistribution: data.activeDaysDistribution || [],
  challengesPerUserDistribution: data.challengesPerUserDistribution || [],
  taskProgressDistribution: data.taskProgressDistribution || [],
  // Weekday rows arrive as { label, volume }; the chart wants counts.
  weekdayPattern: (data.weekdayPattern || []).map((day) => ({
    label: day.label,
    count: day.volume ?? 0
  })),

  leaderboard: data.leaderboard || [],
  topChallenges: data.topChallenges || []
});

export const fetchCompanyChallengesDashboard = async (department = '') => {
  try {
    return adaptDashboard(unwrap(await getCompanyChallengesDashboard(department)));
  } catch (error) {
    console.error('fetchCompanyChallengesDashboard error:', error);
    return null;
  }
};

/** Matches the shared table fetcher contract ({ page, perPage, search }). */
export const fetchCompanyChallengesList = async ({
  page = 1,
  perPage = 10,
  search = '',
  phase = '',
  department = ''
} = {}) => {
  try {
    const data = unwrap(await getCompanyChallengesList(page, perPage, search, phase, department));
    return {
      data: data.data || [],
      totalCount: data.totalCount || 0,
      primaryDataTypeLabel: data.primaryDataTypeLabel || 'Steps',
      unit: data.unit || null
    };
  } catch (error) {
    console.error('fetchCompanyChallengesList error:', error);
    return { data: [], totalCount: 0 };
  }
};

export const fetchCompanyChallengesUsers = async ({
  page = 1,
  perPage = 10,
  search = '',
  department = ''
} = {}) => {
  try {
    const data = unwrap(await getCompanyChallengesUsers(page, perPage, search, department));
    return {
      data: data.data || [],
      totalCount: data.totalCount || 0,
      totalParticipants: data.totalParticipants || 0,
      primaryDataTypeLabel: data.primaryDataTypeLabel || 'Steps',
      unit: data.unit || null
    };
  } catch (error) {
    console.error('fetchCompanyChallengesUsers error:', error);
    return { data: [], totalCount: 0 };
  }
};

export const fetchCompanyChallengeUserDetail = async (userId) => {
  try {
    const data = unwrap(await getCompanyChallengesUserDetail(userId));
    return {
      ...data,
      // The chart wants numbers; the ledger rows carry the target flag too.
      dailySeries: (data.dailySeries || []).map((day) => day.volume ?? 0),
      dailyTargetHits: (data.dailySeries || []).map((day) => !!day.targetHit)
    };
  } catch (error) {
    console.error('fetchCompanyChallengeUserDetail error:', error);
    return null;
  }
};
